import React, {useState} from "react";
import {TrashIcon, PlusIcon, StarIcon} from "@heroicons/react/24/outline";
import {StarIcon as StarIconSolid} from "@heroicons/react/24/solid";
import useTranslate from "../../core/languages/hooks/useTranslate";
import usePaymentCardsGet from "../hooks/usePaymentCardsGet";
import usePaymentCardDelete from "../hooks/usePaymentCardDelete";
import usePaymentCardSetPrimary from "../hooks/usePaymentCardSetPrimary";
import {SavedCard} from "../types/PaymentCard";
import Loader from "../../ui/shared/Loader/Loader";
import CardBrandLogo from "./CardBrandLogo";

// ─── Single card row ──────────────────────────────────────────────────────────

const CardRow: React.FC<{
    card: SavedCard;
    onDelete: (id: string) => void;
    onSetPrimary: (id: string) => void;
    deleting: boolean;
    settingPrimary: boolean;
}> = ({card, onDelete, onSetPrimary, deleting, settingPrimary}) => {
    const t = useTranslate();

    return (
        <div className={`flex items-center gap-4 p-4 bg-white dark:bg-neutral-900 rounded-2xl border transition-colors ${
            card.principal
                ? "border-primary-400 dark:border-primary-500"
                : "border-neutral-200 dark:border-neutral-700"
        }`}>
            {/* Brand logo */}
            <div className="flex flex-shrink-0 items-center justify-center rounded-xl bg-neutral-100 dark:bg-neutral-800 p-1.5">
                <CardBrandLogo brand={card.brand}/>
            </div>

            {/* Card info */}
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                    {card.pan}
                </span>
                    {card.principal && (
                        <span className="text-xs font-semibold text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-500/10 px-2 py-0.5 rounded-full">
                        {t('paymentCard.primary')}
                    </span>
                    )}
                </div>
                <div className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">
                    {card.cardHolder} · {card.expDate}
                </div>
            </div>

            {/* Set primary button */}
            <button
                type="button"
                onClick={() => onSetPrimary(card.uuid)}
                disabled={card.principal || settingPrimary}
                title={card.principal ? t('paymentCard.isPrimary') : t('paymentCard.setPrimary')}
                className="flex-shrink-0 rounded-lg p-1.5 transition-colors disabled:cursor-default
                text-neutral-400 hover:text-yellow-500 hover:bg-yellow-50
                dark:hover:text-yellow-400 dark:hover:bg-yellow-500/10
                disabled:text-yellow-400 dark:disabled:text-yellow-400 disabled:opacity-100"
            >
                {settingPrimary
                    ? <Loader className="h-4 w-4"/>
                    : card.principal
                        ? <StarIconSolid className="h-4 w-4 text-yellow-400"/>
                        : <StarIcon className="h-4 w-4"/>
                }
            </button>
            {/* Delete button */}
            <button
                type="button"
                onClick={() => onDelete(card.uuid)}
                disabled={deleting}
                className="flex-shrink-0 rounded-lg p-1.5 text-neutral-400 hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-500/10 dark:hover:text-red-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
                {deleting
                    ? <Loader className="h-4 w-4"/>
                    : <TrashIcon className="h-4 w-4"/>
                }
            </button>
        </div>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────

interface SavedCardsListProps {
    /** Callback para mostrar el formulario de añadir tarjeta */
    onAddCard: () => void;
    /** Callback para mostrar alerta en el contenedor padre */
    onDeleteError: (message: string) => void;
}

const SavedCardsList: React.FC<SavedCardsListProps> = ({onAddCard, onDeleteError}) => {
    const t = useTranslate();
    const {data: cards, isLoading} = usePaymentCardsGet();
    const deleteCard = usePaymentCardDelete();
    const setPrimary = usePaymentCardSetPrimary();
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [settingPrimaryId, setSettingPrimaryId] = useState<string | null>(null);

    const handleDelete = async (id: string) => {
        setDeletingId(id);
        try {
            await deleteCard(id);
        } catch (err: any) {
            let errorMsg: string | null = null;

            if (err?.message && typeof err.message === "string") {
                errorMsg = err.message;
            } else if (typeof err === "string") {
                errorMsg = err;
            }

            onDeleteError(errorMsg ?? t("common.feedback.unexpected_error"));
        } finally {
            setDeletingId(null);
        }
    };

    const handleSetPrimary = async (id: string) => {
        setSettingPrimaryId(id);
        try {
            await setPrimary(id);
        } finally {
            setSettingPrimaryId(null);
        }
    };

    if (isLoading) {
        return <Loader centerHorizontally/>;
    }

    return (
        <div className="space-y-3">
            {cards && cards.length > 0 ? (
                cards.map(card => (
                    <CardRow
                        key={card.uuid}
                        card={card}
                        onDelete={handleDelete}
                        onSetPrimary={handleSetPrimary}
                        deleting={deletingId === card.uuid}
                        settingPrimary={settingPrimaryId === card.uuid}
                    />
                ))
            ) : (
                <p className="text-sm text-neutral-500 dark:text-neutral-400 py-2">
                    {t("paymentCard.noSavedCards")}
                </p>
            )}

            {/* Add card button */}
            <button
                type="button"
                onClick={onAddCard}
                className="mt-1 flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-neutral-300 dark:border-neutral-700 px-4 py-3 text-sm font-medium text-neutral-500 hover:border-primary-400 hover:text-primary-600 dark:hover:border-primary-500 dark:hover:text-primary-400 transition-colors"
            >
                <PlusIcon className="h-4 w-4"/>
                {t("paymentCard.addCard")}
            </button>
        </div>
    );
};

export default SavedCardsList;
